import {
  generateEstimatedDeliveryGroupLabel,
  generateEstimatedDeliveryGroups,
  generateEstimatedDeliveryGroupTitleLabel
} from '~/components/quote-results/filter/estimated-delivery-group-helper';
import { toLocaleDateString, toLocaleDateStringShort } from './dates';
import { convertMetersToMiles } from './distance';
import { toDisplayCurrency } from './prices';
import {
  Quote,
  QuoteProtection,
  BasketQuote,
  QuoteExtra,
  AvailableExtra,
  QuoteAddress,
  QuoteDates,
  DateType,
  DateElement,
  QuoteParcel,
  BasketItem,
  QuoteExtras,
  Price,
  QuoteParcels,
  Currency
} from './types/basket-types';
import { ShopProviders } from './types/edge-api-types';
import { BasketSections } from './types/enums';
import { Label, Protection, ProtectionPrefix, Upsell } from './types/extra-types';
import { onlyUniqueNonBlank, SumNumber } from './utils/array';
import dayjs from 'dayjs';
import { ConfigSetting } from '@parcel2go/npm-p2g-app';
import { parseParcelsUrlString, QuoteQueryParams } from './utils/parse-parcels-url-string';
import { TFunction } from 'i18next';

export const QuoteTags = {
  RequiresPrinter: 'requiresprinter',
  DropOff: 'dropoff',
  Locker: 'locker',
  ShopDelivery: 'shopdelivery',
  DDP: 'ddp',
  PrintInStoreOnly: 'printinstoreonly',
  SameDay: 'sameday',
  CustomsScores: 'ui:customsscores'
};

// Maximum price uplift in pence allowed for a same-day service to surface as a faster-service upgrade.
// Same-day services are typically much pricier; without this ceiling they produce hostile upgrade suggestions.
export const SAME_DAY_MAX_PRICE_DELTA = 2000; // £20

export function isSameDayTaggedQuote(q: Quote): boolean {
  return q.tags?.some((t) => t.toLowerCase() === QuoteTags.SameDay) ?? false;
}

export const ServiceTypes = ['Collection', 'Drop Off', 'Locker', 'SameDay', 'Shop Delivery'] as const;
export type ServiceType = (typeof ServiceTypes)[number];

export const PrinterTypes = ['Print At Home', 'Print At Store', 'Driver Brings Label'] as const;
export type PrinterType = (typeof PrinterTypes)[number];

export const ToPrintTypeDefaultLabel = (t: PrinterType) => {
  switch (t) {
    case 'Driver Brings Label':
      return 'Courier supplied labels';
    case 'Print At Home':
      return 'Print own labels';
    case 'Print At Store':
      return 'Print labels in-store';
    default:
      return t;
  }
};

export const ToServiceTypeDefaultLabel = (t: ServiceType) => {
  switch (t) {
    case 'Locker':
      return 'Locker services';
    case 'Collection':
      return 'Collection services';
    case 'Drop Off':
      return 'Drop off services';
    case 'SameDay':
      return 'Same day services';
    case 'Shop Delivery':
      return 'Shop delivery services';
    default:
      return t;
  }
};

export const ToServiceTagDefaultLabel = (t: string) => {
  switch (t) {
    case QuoteTags.DDP:
      return 'DDP Available';
    default:
      return t;
  }
};

export const ToDeliveryOptionLabel = (t: number) => {
  switch (t) {
    case 1:
      return 'Next day';
    default:
      return `${t} day`;
  }
};

export const SortTypes = ['Price', 'Fastest', 'Closest', 'Rating'] as const;
export type SortType = (typeof SortTypes)[number];

export type QuoteFilterOptions = {
  serviceType?: ServiceType;
  printerTypes?: PrinterType[];
  sortType?: SortType;
  estimatedDeliveryDays?: number[];
  incVat?: boolean;
  ratings?: number[];
  couriers?: string[];
  extras?: string[];
  tags?: string[];
  deliveredBy?: Date;
  collectedOn?: Date;
};

export function appliedQuoteFilters(filter: QuoteFilterOptions) {
  return SumNumber([
    filter.serviceType ? 1 : 0,
    filter.couriers?.length ?? 0,
    filter.estimatedDeliveryDays?.length ?? 0,
    filter.extras?.length ?? 0,
    filter.printerTypes?.length ?? 0,
    filter.ratings?.length ?? 0,
    filter.deliveredBy ? 1 : 0,
    filter.collectedOn ? 1 : 0
  ]);
}

export function quoteToAnalyticsEvent(quote: Quote, extras: string[] | undefined = undefined) {
  return {
    item_id: quote.service.slug,
    item_name: quote.service.name,
    item_brand: quote.service.courier,
    price: quote.price.net / 100,
    item_category: isQuoteDropOff(quote) ? 'Drop Off' : 'Collection',
    item_category2: doesQuoteRequirePrinter(quote) ? 'Printer Required' : 'Printer Not Required',
    item_category3: quote.metadata?.Group,
    item_category4:
      extras === undefined
        ? undefined
        : extras?.find((x) => x?.startsWith(ProtectionPrefix))?.length !== 0
          ? 'Protection'
          : 'No Protection'
  };
}

export function filterQuoteDatesWithoutExtraPrices(date: DateElement) {
  return !date.extraKey && !date.price;
}

export function filterQuoteDatesWithExtraPrices(date: DateElement) {
  return !!date.extraKey && date.price;
}

export function getQuotesValidCollectionDates(quote: Quote) {
  return quote.dates.filter(
    (x) => x.type === DateType.Collection && x.collection && x.cutoff && dayjs(x.cutoff) > dayjs()
  );
}

export function getBestQuoteDate(quote: Quote, dates: QuoteDates) {
  const collectionDates = quote.dates.filter((x) => x.type == DateType.Collection);
  if (isQuoteDropOff(quote)) {
    return collectionDates[0];
  }
  const validDates = getQuotesValidCollectionDates(quote);
  const next = validDates.filter((x) => !x.extraKey)[0];

  if (!dates.dates || !Array.isArray(dates.dates)) {
    return next;
  }

  const selected = dates.dates.filter((x) => x.type == DateType.Collection)[0];
  if (!selected) {
    return next;
  }
  const match = validDates.filter((x) => x.collection?.date && dayjs(x.collection?.date).isSame(selected.date))[0];
  if (!match) {
    return next;
  }
  return match;
}

export function quoteRequiresCollectionTime(quote: Quote) {
  return quote.service.collectionTimeSupport;
}

export function quoteDateCollectionTimeOptions(date: DateElement): string[] {
  const isToday = dayjs(date.collection?.date).isSame(dayjs(), 'day');
  const referenceDate = date.collection?.date ? dayjs(date.collection.date) : dayjs();

  // Determine start time
  let startTime: dayjs.Dayjs;
  if (isToday) {
    // If today, round up to the next hour, but never before timeMin
    const now = dayjs();
    const nextHour = now.add(1, 'hour').startOf('hour');
    const nextHourTime = referenceDate.hour(nextHour.hour()).minute(nextHour.minute()).second(0).millisecond(0);

    // Calculate minimum allowed time (timeMin or 9:00)
    const timeMin = date.collection?.timeMin || '9:00';
    const [minHours, minMinutes] = timeMin.split(':').map(Number);
    const minTime = referenceDate.hour(minHours).minute(minMinutes).second(0).millisecond(0);

    // Use whichever is later: next hour or minimum time
    startTime = nextHourTime.isAfter(minTime) ? nextHourTime : minTime;
  } else {
    // Use timeMin if available, otherwise default to 9:00
    const timeMin = date.collection?.timeMin || '9:00';
    const [hours, minutes] = timeMin.split(':').map(Number);
    startTime = referenceDate.hour(hours).minute(minutes).second(0).millisecond(0);
  }

  // Determine end time
  const timeMax = date.collection?.timeMax || '17:00';
  const [endHours, endMinutes] = timeMax.split(':').map(Number);
  const endTime = referenceDate.hour(endHours).minute(endMinutes).second(0).millisecond(0);

  // Generate times in 30-minute increments
  const times: string[] = [];
  let currentTime = startTime;

  while (currentTime.isBefore(endTime) || currentTime.isSame(endTime, 'minute')) {
    times.push(currentTime.format('HH:mm'));
    currentTime = currentTime.add(30, 'minute');
  }

  return times;
}

export function doesQuoteHaveTag(quote: Quote, tag: string) {
  return quote.tags.includes(tag);
}

export function areDimensionsOptionalForQuote(quote: Quote) {
  return doesQuoteHaveTag(quote, 'ui:dimensionsoptional');
}

export function showDimensionsForQuote(quote: Quote) {
  return doesQuoteHaveTag(quote, 'ui:showdimensions');
}

export function getTermsLinks(quote: Quote): { courier: string; link: string } | undefined {
  if (quote?.service?.courier && quote.metadata?.['ui:ServiceTermsAndConditionsLink']) {
    return { courier: quote.service.courier, link: `${quote.metadata['ui:ServiceTermsAndConditionsLink']}` };
  }
  return undefined;
}

export function estimatedDeliveryDateMax(quote: Quote | BasketQuote) {
  return new Date(getQuotesValidCollectionDates(quote)[0]?.delivery?.dateMax);
}

export function estimatedDeliveryDateMin(quote: Quote | BasketQuote) {
  return new Date(getQuotesValidCollectionDates(quote)[0]?.delivery?.dateMin);
}

export function doesQuoteForceCollectionAddressSelection(quote: Quote) {
  if (!quote || !quote.metadata) return false;
  return quote?.metadata['ui:ForceAddressSelection'] === 'Collection';
}

export function getDeliveryGuaranteeDate(quote: BasketQuote) {
  const maxDeliveryDate = getQuotesValidCollectionDates(quote)[0]?.delivery?.dateMax;
  return maxDeliveryDate ? new Date(maxDeliveryDate) : null;
}

export function isQuoteDropOff(quote: Quote): boolean {
  return (
    quote?.tags?.some((x) => x === QuoteTags.DropOff || x === QuoteTags.Locker) && !!quote.service?.dropOffProviderGroup
  );
}

export function isQuoteLetterBoxService(quote: Quote): boolean {
  return (
    quote?.tags?.includes('ui:hermespostable') ||
    quote?.service?.name?.toLowerCase().includes('letter') ||
    quote?.service?.name?.toLowerCase().includes('postable') ||
    quote?.metadata?.['Tab:Warning'] === 'DELIVERED THROUGH LETTERBOX'
  );
}

export function isQuoteLockerDropOff(quote: Quote): boolean {
  return quote?.tags?.some((x) => x === QuoteTags.Locker) && !!quote.service?.dropOffProviderGroup;
}

export function isQuoteShopDelivery(quote: Quote): boolean {
  return quote?.tags?.some((x) => x === QuoteTags.ShopDelivery);
}

export function doesQuoteSupportDdp(quote: Quote): boolean {
  return quote?.tags?.some((x) => x === QuoteTags.DDP);
}

export function requiresMobileNumberBasedOnExtrasSelection(
  quote: Quote,
  selectedExtras: QuoteExtra[],
  originAddress: QuoteAddress
): boolean {
  return (
    !originAddress.phone &&
    quote?.availableExtras?.some((x) => x.key === Upsell.SMS) &&
    selectedExtras?.some((x) => x.key === Upsell.SMS)
  );
}

export function isDriverBringsLabels(quote: Quote) {
  return quote.availableExtras.some((x) => x.key === Label.DRIVER);
}

export function isPrintInStore(quote: Quote) {
  return quote.availableExtras.some((x) => x.key === Label.STORE);
}

export function isPrintInStoreOnly(quote: Quote) {
  return doesQuoteHaveTag(quote, QuoteTags.PrintInStoreOnly);
}

export function doesQuoteRequirePrinter(quote: Quote) {
  if (isPrintInStore(quote) || isDriverBringsLabels(quote) || isPrintInStoreOnly(quote)) {
    return false;
  }

  return doesQuoteHaveTag(quote, QuoteTags.RequiresPrinter);
}

export function isSignatureExtrasAvailable(quote: Quote) {
  return quote.availableExtras.some((x) => x.key === Upsell.SIGNATURE);
}

export function isSmsExtrasAvailable(quote: Quote) {
  return quote.availableExtras.some((x) => x.key === Upsell.SMS);
}

export function sortQuotesByDeliveryDate(quotes: Quote[] | BasketQuote[]) {
  return quotes.sort((a, b) => {
    const aTime = estimatedDeliveryDateMax(a).getTime();
    const bTime = estimatedDeliveryDateMax(b).getTime();
    if (aTime === bTime) return a.price.net - b.price.net;
    return aTime - bTime;
  });
}

export function sortQuotesByPrice(quotes: Quote[] | BasketQuote[]) {
  return quotes.sort((a, b) => a.price.net - b.price.net);
}

// Couriers that operate under multiple slugs/names. Used as a fallback when
// service.courierGroup is not yet emitted by the backend.
const KNOWN_COURIER_GROUPS: Array<{ name: string; slugs: string[] }> = [
  { name: 'DHL', slugs: ['dhlparcel', 'dhl'] },
  { name: 'EVRi', slugs: ['hermes', 'myhermes'] },
  { name: 'UPS', slugs: ['ups', 'ups-access-point'] },
  { name: 'Yodel', slugs: ['yodel', 'collectplus'] }
];

export function getCourierGroup(quote: Quote): string | undefined {
  if (quote.service.courierGroup) return quote.service.courierGroup;
  return KNOWN_COURIER_GROUPS.find((g) => g.slugs.includes(quote.service.courierSlug))?.name;
}

export function quotesHaveSameCourier(a: Quote, b: Quote): boolean {
  if (a.service.courierSlug && a.service.courierSlug === b.service.courierSlug) return true;
  if (a.service.courier && a.service.courier === b.service.courier) return true;
  const groupA = getCourierGroup(a);
  const groupB = getCourierGroup(b);
  return Boolean(groupA && groupB && groupA === groupB);
}

export function findFasterService(newQuotes: Quote[] | undefined | null, originalQuote: Quote) {
  if (!newQuotes) return undefined;
  const isDropOff = isQuoteDropOff(originalQuote);
  const deliveryDate = estimatedDeliveryDateMax(originalQuote);
  const requiresPrinter = doesQuoteRequirePrinter(originalQuote);

  let quotes = newQuotes.filter(
    (quote) =>
      (!requiresPrinter || doesQuoteRequirePrinter(quote)) &&
      isQuoteDropOff(quote) === isDropOff &&
      quote.service.slug !== originalQuote.service.slug &&
      estimatedDeliveryDateMax(quote).getTime() < deliveryDate.getTime() &&
      quote.price.net >= originalQuote.price.net &&
      // Same-day services must not exceed the price ceiling above the current service.
      (!isSameDayTaggedQuote(quote) || quote.price.net <= originalQuote.price.net + SAME_DAY_MAX_PRICE_DELTA)
  );

  // Prefer non-same-day candidates; only fall back to same-day when no non-same-day option exists.
  const nonSameDay = quotes.filter((quote) => !isSameDayTaggedQuote(quote));
  if (nonSameDay.length > 0) quotes = nonSameDay;

  quotes = sortQuotesByDeliveryDate(quotes);

  const sameCourier = quotes.find((quote) => quotesHaveSameCourier(quote, originalQuote));
  if (sameCourier) {
    return sameCourier;
  }
  return quotes[0];
}

export const generateLegacyQuoteHref = (
  origin: string,
  destination: string,
  originPostcode: string | undefined,
  destinationPostcode: string | undefined,
  parcels: QuoteParcel[],
  selectedQuote: Quote,
  selectedExtras: string[]
) => {
  const params = [`slug=${selectedQuote.service.slug}`, `col=${origin}`, `dest=${destination}`];

  if (originPostcode) params.push(`cp=${originPostcode}`);

  if (destinationPostcode) params.push(`dp=${destinationPostcode}`);

  let parcelProps = `p=1~${parcels[0].weight}`;

  if (parcels[0].height !== 0 && parcels[0].width !== 0 && parcels[0].length !== 0) {
    parcelProps += `|${parcels[0].length}|${parcels[0].width}|${parcels[0].height}|${parcels[0].value ? Math.round(parseFloat(parcels[0].value.toFixed(2)) * 100) : 0}`;
  }

  if (parcels.length > 1) {
    for (let i = 1; i < parcels.length; i++) {
      parcelProps += `^1~${parcels[i].weight}`;

      if (parcels[i].height !== 0 && parcels[i].width !== 0 && parcels[i].length !== 0) {
        parcelProps += `|${parcels[i].length}|${parcels[i].width}|${parcels[i].height}|${parcels[i].value ? Math.round(parseFloat(parcels[i].value?.toFixed(2) ?? '0') * 100) : 0}`;
      }
    }
  }

  params.push(parcelProps);

  if (selectedExtras?.length) {
    params.push(`u=${selectedExtras.join(',')}`);
  }

  return params.join('&');
};

export const generateBasicQuoteHref = (
  origin: string,
  destination: string,
  weight: number,
  height: number | undefined,
  width: number | undefined,
  length: number | undefined
) => {
  const params = [`coli=${origin}`, `desti=${destination}`];

  if (length) {
    params.push(`length=${length}`);
  }

  if (width) {
    params.push(`width=${width}`);
  }

  if (height) {
    params.push(`height=${height}`);
  }

  if (weight) {
    params.push(`weight=${weight}`);
  }

  return params.join('&');
};

export function findClosestService(newQuotes: Quote[] | undefined | null, originalQuote: Quote, shops: ShopProviders) {
  if (!newQuotes) return undefined;

  const best: { provider: string | null; distance: number } = {
    provider: null,
    distance: Number.MAX_SAFE_INTEGER
  };

  for (const s of Object.getOwnPropertyNames(shops)) {
    if (!newQuotes.some((quote) => quote.service.dropOffProviderGroup === s)) continue;
    if (shops[s][0].distance < best.distance && s !== originalQuote.service.dropOffProviderGroup) {
      best.provider = s;
      best.distance = shops[s][0].distance;
    }
  }

  if (!best.provider) return undefined;

  const originalDistance = shops[originalQuote.service.dropOffProviderGroup]?.[0]?.distance;
  if (originalDistance !== undefined && originalDistance - best.distance < 100) return undefined;

  const deliveryDate = estimatedDeliveryDateMax(originalQuote);
  const requiresPrinter = doesQuoteRequirePrinter(originalQuote);

  let quotes = newQuotes.filter(
    (quote) =>
      (!requiresPrinter || doesQuoteRequirePrinter(quote)) &&
      isQuoteDropOff(quote) &&
      quote.service.dropOffProviderGroup === best.provider &&
      estimatedDeliveryDateMax(quote).getTime() <= deliveryDate.getTime() &&
      quote.price.net >= originalQuote.price.net
  );
  quotes = sortQuotesByPrice(quotes);

  return quotes[0];
}

export function quoteToProtectionOptions(quote: Quote): QuoteProtection {
  return {
    loading: false,
    includedProtection: quote.service.includedProtection,
    options: quote.availableExtras.filter(quoteProtectionExtrasFilter),
    requestId: Math.random().toString(36)
  };
}

export function quoteSurcharges(quote: BasketQuote | null | undefined) {
  if (!quote || !quote.price || !quote.price.breakdown) return [];
  return quote.price.breakdown.filter((x) => x.metadata?.surcharge && x.amount > 0);
}

export function quoteSurchargesTotal(quote: BasketQuote | null | undefined) {
  if (!quote || !quote.price || !quote.price.breakdown) return 0;
  return SumNumber(quote.price.breakdown.filter((x) => x.metadata?.surcharge && x.amount > 0).map((x) => x.amount));
}

export function quoteBasePrice(quote: BasketQuote | null | undefined) {
  if (!quote || !quote.price || !quote.price.breakdown) return quote?.price?.net || 0;
  return SumNumber(quote.price.breakdown.filter((x) => !x.metadata?.surcharge && x.amount > 0).map((x) => x.amount));
}

export function quoteHasMoreSurchages(
  quote: BasketQuote | null | undefined,
  originalQuote: BasketQuote | null | undefined
) {
  return quoteSurcharges(quote).length > quoteSurcharges(originalQuote).length;
}

export function basePriceHasIncreasedForSameNumberParcelsAndIsVolumetric(
  quote: BasketQuote | null | undefined,
  originalQuote: BasketQuote | null | undefined
): boolean {
  return (
    !!quote?.metadata?.VolumetricDivisor &&
    quoteBasePrice(quote) > quoteBasePrice(originalQuote) &&
    quote?.price?.breakdown?.filter((b) => !b.metadata?.surcharge).length ===
      originalQuote?.price?.breakdown?.filter((b) => !b.metadata?.surcharge).length
  );
}

export function shouldShowPriceChangeMessage(
  quote: BasketQuote | null | undefined,
  originalQuote: BasketQuote | null | undefined
) {
  return (
    // We only want to show the popup for volumetric changes. @DG / @GC
    // quoteHasMoreSurchages(quote, originalQuote) ||
    // quoteSurchargesTotal(quote) > quoteSurchargesTotal(originalQuote) ||
    basePriceHasIncreasedForSameNumberParcelsAndIsVolumetric(quote, originalQuote)
  );
}

export function quoteProtectionExtrasFilter(extras: AvailableExtra) {
  return extras.key.startsWith(ProtectionPrefix);
}

export function courierListFromQuotes(quotes: BasketQuote[], sort?: SortType) {
  const result: { logo: string; name: string; count: number; days: number; cheapest: number; slug: string }[] = [];
  quotes.forEach((quote) => {
    const item = result.find((x) => x.name === quote.service.courier);
    const days = quote.dates.length > 0 ? new Date(quote.dates[0].delivery.dateMax).getTime() : Number.MAX_SAFE_INTEGER;
    if (!item) {
      result.push({
        logo: quote.service.slug,
        name: quote.service.courier,
        count: 1,
        days: days,
        cheapest: quote.price.net,
        slug: quote.service.courierSlug
      });
    } else {
      item.count++;
      item.days = Math.min(item.days, days);
      item.cheapest = Math.min(item.cheapest, quote.price.net);
    }
  });

  if (sort === 'Fastest') {
    return result.sort((a, b) => a.days - b.days);
  }

  if (sort === 'Price') {
    return result.sort((a, b) => a.cheapest - b.cheapest);
  }

  return result.sort((a, b) => a.name.localeCompare(b.name));
}

export function courierGroupListFromQuotes(quotes: BasketQuote[]) {
  const result: { logo: string; name: string; count: number; days: number; cheapest: number; slugs: string[] }[] = [];

  quotes.forEach((quote) => {
    const days = quote.dates.length > 0 ? new Date(quote.dates[0].delivery.dateMax).getTime() : Number.MAX_SAFE_INTEGER;
    const groupName = getCourierGroup(quote);

    let item = result.find((x) => x.slugs.includes(quote.service.courierSlug));
    if (!item && groupName) {
      item = result.find((x) => x.name === groupName);
    }

    if (item) {
      if (!item.slugs.includes(quote.service.courierSlug)) {
        item.slugs.push(quote.service.courierSlug);
      }
      item.count++;
      item.days = Math.min(item.days, days);
      item.cheapest = Math.min(item.cheapest, quote.price.net);
      return;
    }

    const knownGroup = groupName ? KNOWN_COURIER_GROUPS.find((g) => g.name === groupName) : undefined;
    result.push({
      logo: quote.service.slug,
      name: groupName ?? quote.service.courier,
      count: 1,
      days,
      cheapest: quote.price.net,
      slugs: knownGroup ? [...knownGroup.slugs] : [quote.service.courierSlug]
    });
  });

  return result.sort((a, b) => a.name.localeCompare(b.name));
}

export function listDeliveryDatesForQuotes(quotes: BasketQuote[]) {
  const result: Date[] = [];
  quotes.forEach((quote) => {
    quote.dates.forEach((quoteDate) => {
      const date = new Date(quoteDate.delivery.dateMax);
      const item = result.find((x) => x.getTime() === date.getTime());
      if (!item) {
        result.push(date);
      }
    });
  });
  return result.sort((a, b) => a.getTime() - b.getTime());
}

export function getRating(quote: BasketQuote): { rating: number; ratingTypes: { name: string; value: number }[] } {
  if (!quote.metadata) {
    return {
      rating: 5,
      ratingTypes: []
    };
  }

  const ratingTypes = Object.keys(quote.metadata || {})
    .filter((x) => x.startsWith('Rating:'))
    .reduce(
      (acc, x) => {
        const t = x.split(':');
        if (t.length === 2) {
          acc.push({ name: t[1], value: quote.metadata![x] as number });
        }
        return acc;
      },
      [] as { name: string; value: number }[]
    );

  if (ratingTypes.length === 0) {
    return {
      rating: 5,
      ratingTypes: ratingTypes
    };
  }

  const parsedRating = parseFloat((ratingTypes.reduce((acc, x) => acc + x.value, 0) / ratingTypes.length).toFixed(1));

  return {
    rating: Math.round(parsedRating * 2) / 2,
    ratingTypes: ratingTypes
  };
}

export function listCollectionDatesForQuotes(quotes: BasketQuote[]) {
  const result: Date[] = [];
  quotes.forEach((quote) => {
    quote.dates.forEach((quoteDate) => {
      if (!quoteDate.collection?.date) return;
      const date = new Date(quoteDate.collection?.date);
      const item = result.find((x) => x.getTime() === date.getTime());
      if (!item) {
        result.push(date);
      }
    });
  });
  return result.sort((a, b) => a.getTime() - b.getTime());
}

export const hasVAT = (quote: BasketQuote) => quote.price.tax > 0;

export const getEstimatedDeliveryDaysMin = (quote: BasketQuote) => quote.metadata?.estimatedDeliveryDaysMin || 0;

export const getMinDeliveryDaysForQuotes = (quotes: BasketQuote[]) =>
  quotes.map(getEstimatedDeliveryDaysMin).filter(onlyUniqueNonBlank);

export function findCheapestQuote(quotes: BasketQuote[]) {
  return [...quotes].sort((a, b) => a.price.net - b.price.net)[0];
}

export function filterQuotes(
  quotes: BasketQuote[],
  filter: QuoteFilterOptions,
  shops: ShopProviders,
  serviceFilter?: string
) {
  let q = [...(quotes || [])];

  if (serviceFilter) {
    q = q.filter((quote) => quote.tags.some((x) => x === `ui:${serviceFilter}`));
  }

  if (filter.couriers?.length) {
    q = q.filter((quote) => filter.couriers?.includes(quote.service.courierSlug));
  }

  if (filter.tags?.length) {
    q = q.filter((quote) => filter.tags?.every((t) => quote.tags.includes(t)));
  }

  if (filter.serviceType) {
    q = q.filter((quote) => {
      if (filter.serviceType === 'Locker') {
        return isQuoteLockerDropOff(quote) && !isQuoteShopDelivery(quote);
      }
      if (filter.serviceType === 'Drop Off') {
        return isQuoteDropOff(quote) && !isQuoteShopDelivery(quote);
      }
      if (filter.serviceType === 'Collection') {
        return !isQuoteDropOff(quote) && !isQuoteShopDelivery(quote);
      }
      if (filter.serviceType === 'Shop Delivery') {
        return isQuoteShopDelivery(quote);
      }
      return true;
    });
  }

  if (filter.printerTypes?.length) {
    q = q.filter((quote) => {
      if (filter.printerTypes?.some((x) => x === 'Driver Brings Label')) {
        return !isQuoteDropOff(quote) && !doesQuoteRequirePrinter(quote);
      }
      if (filter.printerTypes?.some((x) => x === 'Print At Store')) {
        return isQuoteDropOff(quote) && !doesQuoteRequirePrinter(quote);
      }
      return true;
    });
  }

  if (filter.estimatedDeliveryDays?.length) {
    q = q.filter((quote) => filter.estimatedDeliveryDays?.includes(getEstimatedDeliveryDaysMin(quote)));
  }

  if (filter.extras?.length) {
    q = q.filter((quote) => filter.extras?.every((e) => quote.availableExtras.some((x) => x.key === e)));
  }

  if (filter.deliveredBy) {
    q = q.filter((quote) => {
      return quote.dates.some((x) => new Date(x.delivery.dateMax) <= filter.deliveredBy!);
    });
  }

  if (filter.collectedOn) {
    q = q.filter((quote) => {
      return quote.dates.some(
        (x) => x.collection?.date && new Date(x.collection.date).getTime() === filter.collectedOn!.getTime()
      );
    });
  }

  if (filter?.ratings?.length) {
    q = q.filter((quote) => filter.ratings?.some((r) => getRating(quote).rating >= r));
  }

  if (filter.sortType) {
    if (filter.sortType === 'Price') {
      q = q.sort((a, b) => {
        if (a.price.net !== b.price.net) return a.price.net - b.price.net;

        const aTime = estimatedDeliveryDateMin(a).getTime();
        const bTime = estimatedDeliveryDateMin(b).getTime();
        if (aTime !== bTime) return aTime - bTime;

        const aDistance = (shops[a.service.dropOffProviderGroup] || [])[0]?.distance || Number.MAX_SAFE_INTEGER;
        const bDistance = (shops[b.service.dropOffProviderGroup] || [])[0]?.distance || Number.MAX_SAFE_INTEGER;
        return aDistance - bDistance;
      });
    }
    if (filter.sortType === 'Fastest') {
      q = q.sort((a, b) => {
        const aTime = estimatedDeliveryDateMin(a).getTime();
        const bTime = estimatedDeliveryDateMin(b).getTime();
        if (aTime !== bTime) return aTime - bTime;
        if (Object.getOwnPropertyNames(shops).length === 0) return a.price.net - b.price.net;

        const aDistance = (shops[a.service.dropOffProviderGroup] || [])[0]?.distance || Number.MAX_SAFE_INTEGER;
        const bDistance = (shops[b.service.dropOffProviderGroup] || [])[0]?.distance || Number.MAX_SAFE_INTEGER;
        if (aDistance !== bDistance) return aDistance - bDistance;

        return a.price.net - b.price.net;
      });
    }
    if (filter.sortType === 'Closest') {
      q = q.sort((a, b) => {
        const aDistance = (shops[a.service.dropOffProviderGroup] || [])[0]?.distance || Number.MAX_SAFE_INTEGER;
        const bDistance = (shops[b.service.dropOffProviderGroup] || [])[0]?.distance || Number.MAX_SAFE_INTEGER;
        if (aDistance !== bDistance) return aDistance - bDistance;

        return a.price.net - b.price.net;
      });
    }
    if (filter.sortType === 'Rating') {
      q = q.sort((a, b) => {
        const aRating = getRating(a).rating;
        const bRating = getRating(b).rating;
        if (aRating !== bRating) return bRating - aRating;

        return a.price.net - b.price.net;
      });
    }
  }

  return q;
}

export type QuoteGroup = {
  quotes: BasketQuote[];
  defaultTranslation: string;
  values: {
    [key: string]: number | string;
  };
  key: string;
  translationKey: string;
};

function getDistanceTranslationKeys(
  incVat: boolean,
  distance: number,
  isCollection: boolean,
  isDropOver20: boolean
): { translationKey: string; translationDefault: string } {
  if (isCollection) {
    return {
      translationKey: incVat ? 'RESULT_COLLECTION_DISTANCE_TITLE' : 'RESULT_COLLECTION_DISTANCE_EXCVAT_TITLE',
      translationDefault: `Collection from {{cheapest}}`
    };
  }

  if (isDropOver20) {
    return {
      translationKey: incVat ? 'RESULT_OTHER_SHOPS_DISTANCE_TITLE' : 'RESULT_OTHER_SHOPS_DISTANCE_EXCVAT_TITLE',
      translationDefault: `Other drop shops from {{cheapest}}`
    };
  }

  if (distance <= 1) {
    return {
      translationKey: incVat ? 'RESULT_NEAR_DISTANCE_TITLE' : 'RESULT_NEAR_DISTANCE_EXCVAT_TITLE',
      translationDefault: `Your nearest location from {{cheapest}}`
    };
  }

  return {
    translationKey: incVat ? 'RESULT_DISTANCE_TITLE' : 'RESULT_DISTANCE_EXCVAT_TITLE',
    translationDefault: `Drop shops within {{distance}} from {{cheapest}}`
  };
}

export function toQuoteGroups(
  filter: QuoteFilterOptions,
  quotes: BasketQuote[],
  shops: ShopProviders,
  isDomestic: boolean,
  getDistanceDisplay: (distance: number) => string,
  taxName: string
) {
  const output: QuoteGroup[] = [];

  if (!quotes?.length) {
    return output;
  }

  const noPostcode = !shops || Object.getOwnPropertyNames(shops).length === 0;
  const sortType: SortType | undefined = noPostcode && filter.sortType === 'Closest' ? 'Price' : filter.sortType;

  const incExc = filter.incVat ? `<0>Inc ${taxName}</0>` : `<0>Exc ${taxName}</0>`;

  switch (sortType) {
    case 'Price': {
      const chunkSize = 10;
      const quoteChunks = [];
      for (let i = 0; i < quotes.length; i += chunkSize) {
        quoteChunks.push(quotes.slice(i, i + chunkSize));
      }

      quoteChunks.forEach((chunk, index) => {
        const cheapest = findCheapestQuote(chunk);
        output.push({
          translationKey: filter.incVat ? `RESULT_PRICE_INC_${taxName}_TITLE` : `RESULT_PRICE_EXC_${taxName}_TITLE`,
          defaultTranslation: `Services from {{cheapest}} ${incExc}`,
          key: `PRICE-${index}`,
          quotes: chunk,
          values: {
            count: chunk.length,
            cheapest: toDisplayCurrency(cheapest.currency, filter.incVat ? cheapest.price.gross : cheapest.price.net)
          }
        });
      });

      break;
    }

    case 'Rating': {
      const quoteChunks = quotes.reduce<Record<number, BasketQuote[]>>((acc, quote) => {
        const rating = Math.floor(getRating(quote)?.rating ?? 5);
        (acc[rating] ||= []).push(quote);
        return acc;
      }, {});

      Object.keys(quoteChunks)
        .sort((a, b) => +b - +a)
        .map((x) => +x)
        .forEach((rating) => {
          const cheapest = findCheapestQuote(quoteChunks[rating]);
          output.push({
            translationKey: filter.incVat ? `RESULT_RATING_TITLE` : `RESULT_RATING_EXCVAT_TITLE`,
            defaultTranslation: `{{rating}} Star from {{cheapest}} ${incExc}`,
            key: `RATING-${rating}`,
            quotes: quoteChunks[rating],
            values: {
              count: quoteChunks[rating].length,
              rating: rating,
              cheapest: toDisplayCurrency(cheapest.currency, filter.incVat ? cheapest.price.gross : cheapest.price.net)
            }
          });
        });

      break;
    }

    case 'Fastest': {
      const quoteChunks = generateEstimatedDeliveryGroups(quotes, isDomestic).reduce<Record<string, BasketQuote[]>>(
        (acc, day) => {
          acc[day.translationKey] = quotes.filter((q) => day.days.includes(getEstimatedDeliveryDaysMin(q)));
          return acc;
        },
        {}
      );

      Object.keys(quoteChunks).forEach((groupTranslationKey) => {
        const cheapest = findCheapestQuote(quoteChunks[groupTranslationKey]);
        const title = generateEstimatedDeliveryGroupTitleLabel(groupTranslationKey);

        output.push({
          translationKey: filter.incVat ? title.titleTranslationKey : title.titleExcVatTranslationKey,
          defaultTranslation: `{{category}} Delivery from {{cheapest}} ${incExc}`,
          key: `CATEGORY-${groupTranslationKey}`,
          quotes: quoteChunks[groupTranslationKey],
          values: {
            count: quoteChunks[groupTranslationKey].length,
            category: generateEstimatedDeliveryGroupLabel(groupTranslationKey),
            cheapest: toDisplayCurrency(cheapest.currency, filter.incVat ? cheapest.price.gross : cheapest.price.net)
          }
        });
      });

      break;
    }

    case 'Closest': {
      const collectionDistance = -1;
      const over20Distance = -2;

      const quoteChunks = quotes.reduce<Record<number, BasketQuote[]>>((acc, quote) => {
        // Collection Service Group
        if (!isQuoteDropOff(quote)) {
          (acc[collectionDistance] ||= []).push(quote);
          return acc;
        }

        // No Shops - Over 20 Miles?
        if (!shops[quote.service.dropOffProviderGroup]) {
          (acc[over20Distance] ||= []).push(quote);
          return acc;
        }

        // Add to a group based on distance
        const aDistance = (shops[quote.service.dropOffProviderGroup] || [])[0]?.distance || Number.MAX_SAFE_INTEGER;
        const distance = Math.ceil(convertMetersToMiles(aDistance));
        (acc[distance] ||= []).push(quote);
        return acc;
      }, {});

      Object.keys(quoteChunks)
        .sort((a, b) => +b + +a)
        .map((x) => +x)
        .forEach((distance) => {
          const cheapest = findCheapestQuote(quoteChunks[distance]);
          const tKeys = getDistanceTranslationKeys(
            !!filter.incVat,
            distance,
            distance === collectionDistance,
            distance === over20Distance
          );

          output.push({
            translationKey: tKeys.translationKey,
            defaultTranslation: `${tKeys.translationDefault} ${incExc}`,
            key: `DISTANCE-${distance}`,
            quotes: quoteChunks[distance],
            values: {
              count: quoteChunks[distance].length,
              distance: getDistanceDisplay(distance),
              cheapest: toDisplayCurrency(cheapest.currency, filter.incVat ? cheapest.price.gross : cheapest.price.net)
            }
          });
        });

      break;
    }

    default: {
      const cheapest = findCheapestQuote(quotes);
      output.push({
        translationKey: filter.incVat ? `RESULT_DEFAULT_TITLE` : `RESULT_DEFAULT_EXCVAT_TITLE`,
        defaultTranslation: `{{count}} Services from {{cheapest}} ${incExc}`,
        key: 'DEFAULT',
        quotes: quotes,
        values: {
          count: quotes.length,
          cheapest: toDisplayCurrency(cheapest.currency, filter.incVat ? cheapest.price.gross : cheapest.price.net)
        }
      });
    }
  }

  return output;
}

export function nextCollectionDate(dates: DateElement[]) {
  if (!dates || dates.length === 0) {
    return;
  }

  const now = dayjs();

  const collectionDates = dates.filter(
    (x) => x.type === DateType.Collection && x.collection && x.cutoff && dayjs(x.cutoff) > now
  );

  return collectionDates[0];
}

export function maxDimensionsString(quote: BasketQuote, t: TFunction<[string], undefined>) {
  const maxWeight = quote.restrictions?.maxWeight ?? 0;
  const maxLength = quote.restrictions?.maxLength ?? 0;
  const maxWidth = quote.restrictions?.maxWidth ?? 0;
  const maxHeight = quote.restrictions?.maxHeight ?? 0;
  const dimensionCollection = [maxLength, maxWidth, maxHeight].filter((x) => x > 0);
  const dimensions = dimensionCollection.length > 0 ? dimensionCollection.join('x') : '';

  if (maxWeight > 0) {
    if (dimensionCollection.length == 0) {
      return t('BASKET_RESTRICTIONS_WEIGHT_ONLY', { defaultValue: 'Max: {{maxWeight}}kg', maxWeight: maxWeight });
    } else if (dimensionCollection.length == 1) {
      return t('BASKET_RESTRICTIONS_WEIGHT_AND_LENGTH', {
        defaultValue: 'Max: {{maxWeight}}kg - Length: {{dimensions}} cm',
        maxWeight: maxWeight,
        dimensions: dimensions
      });
    } else {
      return t('BASKET_RESTRICTIONS_WEIGHT_AND_DIMENSIONS', {
        defaultValue: 'Max: {{maxWeight}}kg - {{dimensions}} cm',
        maxWeight: maxWeight,
        dimensions: dimensions
      });
    }
  } else {
    if (dimensionCollection.length == 1) {
      return t('BASKET_RESTRICTIONS_LENGTH_ONLY', {
        defaultValue: 'Length: {{dimensions}} cm',
        dimensions: dimensions
      });
    } else {
      return t('BASKET_RESTRICTIONS_DIMENSIONS', { defaultValue: 'Max: {{dimensions}} cm', dimensions: dimensions });
    }
  }
}

export type QuoteQueryParamsModel = {
  quoteType: 'Default' | 'SameDay';
  origin: string;
  originPostcode: string | undefined;
  parcels: QuoteParcel[];
  destination: string;
  destinationPostcode: string | undefined;
  filter: string | undefined;
  couriers: string[] | undefined;
  enforcedVatMode: 'inc' | 'exc' | undefined;
};

export const quoteServiceType = (
  routeServiceType: ServiceType | undefined,
  quoteType: 'Default' | 'SameDay'
): ServiceType | undefined => {
  if (quoteType === 'SameDay') return 'SameDay' as ServiceType;

  return routeServiceType;
};

export const quoteSortType = (serviceType: ServiceType | undefined, routeSortType: SortType | undefined): SortType => {
  if (!serviceType && !routeSortType) return 'Price' as SortType;

  if (serviceType === 'SameDay' && routeSortType === 'Fastest') return 'Price' as SortType;

  if (serviceType === 'Collection' && routeSortType === 'Closest') return 'Price' as SortType;

  return routeSortType || 'Price';
};

export const quoteEstimatedDeliveryDays = (
  serviceType: ServiceType | undefined,
  estimatedDeliveryDays: number[] | undefined
): number[] => {
  if (!serviceType && !estimatedDeliveryDays) return [];

  if (serviceType === 'SameDay') return [];

  return estimatedDeliveryDays || [];
};

export function quoteRequestParams(
  searchParams: URLSearchParams,
  settings: Record<string, string>
): QuoteQueryParamsModel {
  const qs = Object.fromEntries(searchParams.entries());

  const {
    cp: originPostcode,
    [QuoteQueryParams.CollectionCountryIso]: originCountry,
    [QuoteQueryParams.DestinationCountryIso]: destinationCountry,
    dp: destinationPostcode,
    p: parcelsText,
    filter: filter,
    couriers,
    enforcedVatMode,
    quoteType
  } = qs;

  const o = originCountry || settings[ConfigSetting.DEFAULT_ORIGIN_COUNTRY] || 'GBR';

  return {
    origin: o,
    originPostcode: originPostcode || undefined,
    parcels: parseParcelsUrlString(parcelsText, false, o),
    destination: destinationCountry || settings[ConfigSetting.DEFAULT_DESTINATION_COUNTRY] || 'GBR',
    destinationPostcode: destinationPostcode || undefined,
    filter: filter || undefined,
    couriers: couriers && couriers.length > 0 ? couriers.split(',').map((c) => c.toLowerCase().trim()) : undefined,
    enforcedVatMode: ['inc', 'exc'].includes(enforcedVatMode) ? (enforcedVatMode as 'inc' | 'exc') : undefined,
    quoteType: (quoteType || 'Default') as 'Default' | 'SameDay'
  };
}

export function isDomestic(searchParams: URLSearchParams, settings: Record<string, string>): boolean {
  const quoteRequest = quoteRequestParams(searchParams, settings);

  return quoteRequest.origin === quoteRequest.destination;
}

export function isSameDayDelivery(quote: BasketQuote): boolean {
  const deliveryDate = quote.dates[0]?.delivery;
  if (!deliveryDate) return false;
  return deliveryDate.dateMin === deliveryDate.dateMax && dayjs(deliveryDate.dateMax).isSame(dayjs(), 'day');
}

export function classifyDeliveryTag(quote: BasketQuote, isDomestic: boolean): string | undefined {
  if (isSameDayDelivery(quote)) {
    return 'Same Day';
  }

  const deliveryGroups = ['fast', 'medium', 'slow'];
  const deliveryGroup = (quote.metadata?.Group ?? '').toString().toLowerCase();

  if (deliveryGroups.includes(deliveryGroup)) {
    const classification: Record<string, string> = {
      fast: isDomestic ? 'Next Day' : 'Express',
      medium: isDomestic ? '2+ Day' : 'Economy',
      slow: isDomestic ? '3+ Day' : 'Super Economy'
    };

    return classification[deliveryGroup];
  }

  return undefined;
}

export function estimatedDeliveryDateString(quote: BasketQuote) {
  const deliveryDate = quote.dates[0]?.delivery;
  if (!deliveryDate) return;

  if (deliveryDate.dateMin === deliveryDate.dateMax) {
    return toLocaleDateString(quote.dates[0].delivery.dateMin);
  }

  return (
    toLocaleDateStringShort(quote.dates[0].delivery.dateMin) +
    ' - ' +
    toLocaleDateStringShort(quote.dates[0].delivery.dateMax)
  );
}

export function quoteProtectionOptions(quote: BasketQuote) {
  const extra = quote.availableExtras.find((x) => x.key === Protection.BASE);
  const full = quote.availableExtras.find((x) => x.key === Protection.FULL);
  const protection = extra && full ? full : extra;
  return {
    net: protection?.price.net || 0,
    gross: protection?.price.gross || 0,
    available: protection !== undefined && (protection?.price.net || 0) > 0,
    included: protection?.metadata?.protectedAmount || 0,
    max: protection?.metadata?.maxProtection || 0,
    full: extra && full && (full.metadata?.protectedAmount || 0) > (extra?.metadata?.protectedAmount || 0)
  };
}

export function fullProtectionPrice(basketItems: { name: string; item: BasketItem }[]) {
  return basketItems.reduce(
    (
      acc: {
        protection: number;
        value: number;
        price: Price;
        currency: Currency | null;
        changes: { id: string; extra: string }[];
      },
      item
    ) => {
      //Find protection for this quote
      const quote = item.item.sections[BasketSections.QUOTE]?.payload as BasketQuote;
      acc.currency = quote.currency;
      const availableProtection = quote.availableExtras?.filter((e) => e.key.startsWith(ProtectionPrefix));

      //If no protection available, return
      if (!availableProtection || availableProtection.length === 0) {
        return acc;
      }

      //Total value of all parcels
      const parcels = item.item.sections[BasketSections.PARCELS]?.payload as QuoteParcels;
      acc.value += parcels.parcels.reduce((acc, x) => acc + (x.value || 0), 0);

      //Find the best protection for this quote.
      const bestProtection = availableProtection.sort((a, b) => b.price.net - a.price.net)[0];

      //Find the protection the user has already selected.
      const extras = item.item.sections[BasketSections.EXTRAS]?.payload as QuoteExtras;
      const selected = extras?.extras?.find((e) => e.key.startsWith(ProtectionPrefix));
      const existingProtection = availableProtection.find((e) => e.key === selected?.key);

      //If the user hasn't selected a protection, add the best protection to the total.
      if (!selected || !existingProtection) {
        acc.price.net += bestProtection.price.net;
        acc.price.gross += bestProtection.price.gross;
        acc.price.tax += bestProtection.price.tax;
        // Included protection amount will just be the included protection if no extra selected
        acc.protection += quote.service.includedProtection || 0;
        acc.changes.push({ id: item.name, extra: bestProtection.key });
        return acc;
      }

      acc.protection += existingProtection.metadata?.protectedAmount || 0;

      if (existingProtection.price.net < bestProtection.price.net) {
        acc.price.net += bestProtection.price.net - existingProtection.price.net;
        acc.price.gross += bestProtection.price.gross - existingProtection.price.gross;
        acc.price.tax += bestProtection.price.tax - existingProtection.price.tax;
        acc.changes.push({ id: item.name, extra: bestProtection.key });
        return acc;
      }

      return acc;
    },
    { protection: 0, value: 0, price: { net: 0, gross: 0, tax: 0 }, currency: null, changes: [] }
  );
}

export function shouldApplyAutoSignature(
  isAuthenticated: boolean,
  isInternational: boolean,
  quote: BasketQuote,
  extras: string[]
) {
  if (isAuthenticated) {
    return false;
  }

  if (isInternational) {
    return false;
  }

  if (!doesQuoteHaveTag(quote, 'ui:autosignature')) {
    return false;
  }

  if (!isSignatureExtrasAvailable(quote)) {
    return false;
  }

  if (extras.includes(Upsell.SIGNATURE)) {
    return false;
  }

  if (!extras.includes(Protection.BASE) && !extras.includes(Protection.FULL)) {
    return false;
  }

  return true;
}

export type UpgradeType = 'All' | 'SameCourier' | 'None';

export function getQuoteUpgradeOptions(quote: BasketQuote): UpgradeType {
  if (!quote || quote.metadata?.['ui:ShowServiceUpgrade'] === 'None') {
    return 'None';
  }

  if (quote.metadata?.['ui:ShowServiceUpgrade'] === 'SameCourier') {
    return 'SameCourier';
  }

  if (quote.metadata?.['ui:ShowServiceUpgrade'] === 'All') {
    return 'All';
  }

  return 'None';
}
